import { CellContext, ColumnDef } from "@tanstack/react-table";
import { PlusInSquareIcon, TrashAltIcon } from "assets/icons";
import { useLanguage } from "hooks";
import { t } from "i18next";
import { ProductsListItem } from "modules/products";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { IconButton, ItemsTable } from "ui";

import { ProductCell, ProductColorLabel } from "../../../components";
import { ProductsSelectorTableProps } from "../ProductsSelector.types";
import { SizesCell } from "./SizesCell";

import styles from "../ProductsSelector.module.scss";

export const ProductsSelectorTable = ({
  products,
  isLoading = false,
  onlyProductInfoColumn = false,
  value,
  onChange,
  maxValueLength = Infinity
}: ProductsSelectorTableProps) => {
  const { currentLanguage } = useLanguage();

  const columns: Array<ColumnDef<ProductsListItem, ProductsListItem[]>> =
    useMemo(
      () => [
        {
          header: t("Product"),
          size: 60,
          minSize: 60,
          cell: ({ row }) => (
            <Link
              className={styles.productsTable_productCell}
              target="_blank"
              to={`/products/details/${products[row.index].categoryId}/${products[row.index].productId}/${products[row.index].id}/boxes`}
            >
              <ProductCell
                name={row.original.name}
                image={row.original.image}
                visibility={row.original.visibility}
                subtitle={
                  <>
                    <span>{products[row.index].categoryName}</span>
                    <span>·</span>
                    <ProductColorLabel
                      colorValue={products[row.index].colorAttributeValue.value}
                    />
                  </>
                }
              />
            </Link>
          )
        },
        ...(onlyProductInfoColumn
          ? []
          : [
              {
                header: t("Available Sizes"),
                size: 35,
                minSize: 35,
                cell: ({
                  row
                }: CellContext<ProductsListItem, ProductsListItem[]>) => (
                  <SizesCell product={products[row.index]} />
                )
              }
            ]),
        ...(onChange
          ? [
              {
                accessorKey: "iconColumn",
                header: () => null,
                enableSorting: false,
                size: 5,
                minSize: 5,
                cell: (
                  cell: CellContext<ProductsListItem, ProductsListItem[]>
                ) => {
                  if (!value) return null;

                  const isSelected = value.some(
                    (selectedProduct) =>
                      selectedProduct.id === products[cell.row.index].id
                  );

                  const isDisabled =
                    !isSelected && maxValueLength <= value.length;

                  const handleClick = () => {
                    if (isSelected) {
                      onChange?.(
                        value.filter(
                          (selectedProduct) =>
                            selectedProduct.id !== products[cell.row.index].id
                        )
                      );
                    } else {
                      onChange?.([...value, products[cell.row.index]]);
                    }
                  };

                  return (
                    <IconButton
                      disabled={isDisabled}
                      className={styles.iconButton}
                      icon={
                        isSelected ? (
                          <TrashAltIcon fill="#FF0033" width={24} height={24} />
                        ) : (
                          <PlusInSquareIcon stroke="red" />
                        )
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleClick();
                      }}
                    />
                  );
                }
              }
            ]
          : [])
      ],
      [products, value, isLoading, maxValueLength, currentLanguage]
    );

  return (
    <ItemsTable
      className={styles.productsTable}
      columns={columns}
      data={products}
      isLoading={isLoading}
      skeletonHeight={75}
    />
  );
};
